import type {
  AcquisitionOffer,
  PricingCatalogOfferCoupon,
} from "../../services/pricingCatalog/types";

export { resolveCheckoutPlanId } from "../../services/checkout/checkoutMode";

export type CadastroPlanCycle = "monthly" | "one_time" | "annually";

export type CadastroPlan = {
  id: string;
  gateway_id: number;
  name: string;
  amount: string;
  type: CadastroPlanCycle;
};

export type CadastroV2Plan = CadastroPlan & {
  internal_id: string;
};

export const DEGUSTACAO_PLAN_ID = "degustacao";
export const DEGUSTACAO_MIGRATION_MESSAGE =
  "Após 30 dias, sua assinatura será migrada automaticamente da Degustação para o plano One.";

// a oferta equivalente à Degustação no catálogo da API vem com tier
// "preview" (types.ts, PricingCatalogTier), não com o slug hardcoded "degustacao".
const PRICING_CATALOG_PREVIEW_TIER_ID = "preview";

export const isDegustacaoPlan = (
  plan?: Pick<CadastroPlan, "id"> | null,
): boolean =>
  plan?.id === DEGUSTACAO_PLAN_ID || plan?.id === PRICING_CATALOG_PREVIEW_TIER_ID;

export const getPlanUnitLabel = (cycle?: CadastroPlanCycle): string => {
  if (cycle === "monthly") return "/mês";
  if (cycle === "annually") return "/ano";
  if (cycle === "one_time") return " pagamento único";
  return "";
};

export type CadastroApiPlanCoupon = {
  code: string;
  amount: string;
  oldAmount: string;
  description: string | null;
  subtitle: string | null;
};

export type CadastroApiPlan = CadastroV2Plan & {
  coupon: CadastroApiPlanCoupon | null;
};

const formatAmountBRL = (amount: number): string =>
  amount.toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

export const mapAcquisitionOfferToCadastroPlan = (
  offer: AcquisitionOffer,
): CadastroApiPlan => ({
  id: offer.tier,
  internal_id: offer.tier,
  gateway_id: offer.plan.gateway_id,
  name: offer.tier_display_name,
  amount: formatAmountBRL(offer.amount),
  type: offer.billing_type,
  coupon: offer.coupon
    ? {
        code: offer.coupon.code,
        amount: formatAmountBRL(offer.coupon.amount),
        oldAmount: formatAmountBRL(offer.coupon.old_amount),
        description: offer.coupon.description,
        subtitle: offer.coupon.subtitle,
      }
    : null,
});

// combina is_valid (ainda de /coupons/validate) com o
// plan_id/gateway_plan_id do coupon do catálogo novo, sem alterar resolveCheckoutPlanId em si.
export const buildCatalogCheckoutCoupom = ({
  isValid,
  catalogCoupon,
}: {
  isValid: boolean;
  catalogCoupon: PricingCatalogOfferCoupon | null | undefined;
}): { is_valid: boolean; plan_id: number | undefined } => ({
  is_valid: isValid,
  plan_id: catalogCoupon?.gateway_plan_id ?? undefined,
});

// /coupons/validate (legado, sem tipo TS — consumido só por .js) devolve mais campos
// do que os dois helpers abaixo leem (plan_id, invalid_reason, trial_days, etc.); o índice evita
// exigir o shape inteiro pra só poder repassar o objeto de verdade sem excess-property error.
export type CheckoutValidateCoupom = {
  is_valid?: boolean;
  [key: string]: unknown;
};

// idem para o plano selecionado: quem chama passa o objeto de plano de verdade
// (CadastroPlan/CadastroApiPlan, com gateway_id etc.) ou, no card único do cupom legado, o próprio
// objeto de /coupons/validate — só o `id` é lido aqui.
export type CheckoutSelectedPlan = {
  id?: string;
  [key: string]: unknown;
};

// mesmo predicado do Cadastro v2 (`useCheckoutFlow.resolveCoupomForCheckout`):
// cupom válido e ausente do catálogo (legado) cai no objeto de /coupons/validate,
// que já traz plan_id = gateway_plan_id — sem reconciliar num catálogo estático.
export const resolveCoupomForCheckout = ({
  coupom,
  catalogCoupon,
}: {
  coupom?: CheckoutValidateCoupom | null;
  catalogCoupon?: PricingCatalogOfferCoupon | null;
}) =>
  !coupom?.is_valid || catalogCoupon
    ? buildCatalogCheckoutCoupom({
        isValid: Boolean(coupom?.is_valid),
        catalogCoupon,
      })
    : coupom;

export const couponBecomesSelectedPlan = (
  coupom?: { is_jussub?: boolean; [key: string]: unknown } | null,
): boolean => !coupom?.is_jussub;

type SelectorPlan = { id: string; [key: string]: unknown };

// No Jussub o cupom desconta o plano SELECIONADO (não vira um plano fixo como no
// Jusbill). Em vez de um card separado, injetamos o preço com desconto no card do
// plano escolhido dentro do grid, no shape que o CadastroPlanSelector já entende
// (plan.coupon.oldAmount/amount). Só o plano selecionado ganha o desconto; os demais
// seguem no preço cheio. Se não for cupom Jussub válido, devolve os planos intactos.
export const applyJussubCoupomToSelectorPlans = ({
  plans,
  selectedPlanId,
  coupom,
}: {
  plans: SelectorPlan[];
  selectedPlanId?: string | null;
  coupom?: {
    is_valid?: boolean;
    is_jussub?: boolean;
    code?: string;
    amount?: string;
    old_amount?: string;
    [key: string]: unknown;
  } | null;
}): SelectorPlan[] => {
  if (!coupom?.is_valid || !coupom?.is_jussub || !selectedPlanId) {
    return plans;
  }
  return plans.map(plan =>
    plan.id === selectedPlanId
      ? {
          ...plan,
          coupon: {
            code: coupom.code,
            amount: coupom.amount,
            oldAmount: coupom.old_amount,
          },
        }
      : plan,
  );
};

export const resolveCheckoutPlanAndCoupom = ({
  selectedPlan,
  coupom,
  pricingCatalogOffers,
}: {
  selectedPlan?: CheckoutSelectedPlan | null;
  coupom?: CheckoutValidateCoupom | null;
  pricingCatalogOffers?: Pick<
    AcquisitionOffer,
    "tier" | "plan" | "coupon"
  >[] | null;
}) => {
  const selectedOffer = (pricingCatalogOffers || []).find(
    offer => offer.tier === selectedPlan?.id
  );

  return {
    plan: selectedOffer?.plan ?? selectedPlan,
    coupomForCheckout: resolveCoupomForCheckout({
      coupom,
      catalogCoupon: selectedOffer?.coupon,
    }),
  };
};

// um cupom válido em /coupons/validate mas ausente do catálogo novo NÃO é
// mais divergência: a migração pro catálogo novo nunca vai cobrir todo cupom/plano legado, então
// esse é o estado esperado e permanente pra esse universo (fica de pé no tier base, ver
// isCouponMatchedInCatalog). Só sobra divergência real quando o catálogo aceita um cupom que
// /coupons/validate rejeita — aí sim as duas fontes discordam sobre algo que o catálogo julga
// migrado, o que é inconsistência de dado, não legado.
export const hasCatalogCoupomDivergence = ({
  coupomCode,
  isValidateCoupomValid,
  isValidateCoupomLoading,
  isCatalogCouponAccepted,
}: {
  coupomCode: string | null | undefined;
  isValidateCoupomValid: boolean;
  isValidateCoupomLoading: boolean;
  isCatalogCouponAccepted: boolean;
}): boolean => {
  if (!coupomCode) return false;
  // enquanto a saga LOAD_COUPOM (/coupons/validate) ainda está em voo,
  // isValidateCoupomValid reflete o cupom anterior (ou o default), não o que está sendo digitado
  // agora; comparar contra esse valor obsoleto produz falso bloqueio no link OAB.
  if (isValidateCoupomLoading) return false;
  if (isCatalogCouponAccepted && !isValidateCoupomValid) return true;
  return false;
};

// o card único do cupom (fora do grid de tiers) só cobre um cupom
// legado que a API não conhece; um cupom migrado pro catálogo novo continua no grid, com o
// desconto por tier que CadastroPlanSelector já sabe exibir (offer.coupon).
export const isCouponMatchedInCatalog = ({
  coupomCode,
  offers,
}: {
  coupomCode: string | null | undefined;
  offers: Pick<AcquisitionOffer, "coupon">[] | null | undefined;
}): boolean => {
  if (!coupomCode) return false;
  const normalizedCode = coupomCode.trim().toUpperCase();
  return (offers || []).some(
    offer => offer.coupon?.code?.trim().toUpperCase() === normalizedCode,
  );
};

// cupom inválido (seja erro reportado pelo próprio catálogo, seja
// divergência entre /coupons/validate e o catálogo) é entrada do usuário, não falha do catálogo:
// não pode virar erro bloqueante que desmonta o Step2 (e o campo de cupom, com seu próprio
// feedback inline, dentro dele). Os dois casos identificados como "causa é o cupom" fazem
// self-heal (dropar o cupom e seguir sem ele — ver usePricingCatalogAcquisition.rejectCoupon)
// em vez de bloquear; só um erro de catálogo que não seja sobre cupom bloqueia.
export const isPricingCatalogBlockingError = ({
  isPricingCatalogQueryError,
  pricingCatalogErrorCode,
}: {
  isPricingCatalogQueryError: boolean;
  pricingCatalogErrorCode: string | undefined;
}): boolean => {
  if (!isPricingCatalogQueryError) return false;
  return pricingCatalogErrorCode !== "PRICING_CATALOG_COUPON_INVALID";
};

// reconcilia por id numa lista dinâmica (catálogo da API, cuja identidade
// muda a cada cupom/loading): não reage à troca de referência do array como se o plano tivesse
// sumido do catálogo.
export const reconcileCatalogPlan = <
  T extends { id: string; coupon?: { code?: string | null } | null }
>({
  plans,
  selectedPlan,
  planParam,
  coupomCode,
}: {
  plans: T[];
  selectedPlan?: Pick<T, "id"> | null;
  planParam?: string | null;
  coupomCode?: string | null;
}): T | undefined => {
  if (!plans.length) return undefined;

  // o cupom digitado é dono de um tier específico do catálogo (ex.:
  // "CAASP_DEGUSTACAO" só desconta a Degustação) — prevalece sobre a seleção anterior, senão o tier
  // escolhido antes de trocar de cupom ficava selecionado, mostrando o desconto do tier errado.
  if (coupomCode) {
    const normalizedCode = coupomCode.trim().toUpperCase();
    const couponPlan = plans.find(
      plan => plan.coupon?.code?.trim().toUpperCase() === normalizedCode,
    );
    if (couponPlan) return couponPlan;
  }

  if (planParam) {
    const fromParam = plans.find(plan => plan.id === planParam);
    if (fromParam) return fromParam;
  }

  if (selectedPlan?.id) {
    const fromSelected = plans.find(plan => plan.id === selectedPlan.id);
    if (fromSelected) return fromSelected;
  }

  return plans.find(plan => plan.id === "one") || plans[0];
};
